import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  LoadingController,
  ModalController,
  NavController,
  NavParams,
  ViewController,
} from "ionic-angular";
import { DocManagementDataService } from "providers/doc-management-data-service/doc-management-data-service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { forkJoin } from "rxjs/observable/forkJoin";
import { of } from "rxjs/observable/of";
import { concatMap } from "rxjs/operators";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { GenerateAISummary } from "../generate-ai-summary-modal/generate-ai-summary-modal";

export interface DocumentGridItem {
  name: string;
  category: string;
  uploadedBy: string;
  dateTime: string;
  id: number;
}

@Component({
  selector: "patient-documents",
  templateUrl: "patient-documents.html",
})
export class PatientDocumentsComponent implements OnInit {
  @Output() refreshDocumentList: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Input() patientChartDetailsId: number;
  @Input() externalPatientChartId: number;
  @Input() documentList: DocumentGridItem[] = [];

  @ViewChild("inputFile") inputFile: ElementRef;

  documentsForm: FormGroup;
  get isOtherSelected() {
    return this.documentsForm.get("category").value === "Other";
  }
  get uploadFileList(): File[] {
    return this.documentsForm.get("files").value;
  }

  spinner = null;
  spinnerCounter = 0;

  documentsSelected: number[] = [];
  aiReferralSummaryEnabled: boolean = false;

  constructor(
    public navCtrl: NavController,
    public viewCtrl: ViewController,
    public navParams: NavParams,
    public _fb: FormBuilder,
    public referralService: ReferralsServiceProvider,
    public stateData: StateManagementService,
    public utilityService: UtilityService,
    private documentService: DocManagementDataService,
    public loadingCtrl: LoadingController,
    private modalCtrl: ModalController
  ) {
    this.documentsForm = new FormGroup({
      name: new FormControl("", Validators.required),
      category: new FormControl("", Validators.required),
      categoryOther: new FormControl("", Validators.required),
      description: new FormControl("", Validators.required),
      files: new FormControl(null, Validators.required),
    });
    this.patientChartDetailsId = navParams.get("patientId");
  }

  ngOnInit() {
    this.stateData.showAIReferralSummary.subscribe((enabled) => {
      this.aiReferralSummaryEnabled = !!enabled;
    });
  }

  changeCategory() {
    if (this.isOtherSelected) {
      this.documentsForm
        .get("categoryOther")
        .setValidators(Validators.required);
    } else {
      this.documentsForm.patchValue({ categoryOther: "" });
      this.documentsForm.get("categoryOther").clearValidators();
    }
    this.documentsForm.get("categoryOther").updateValueAndValidity();
  }

  selectFiles(e: Event) {
    const { files } = e.target as HTMLInputElement;
    const newFiles: File[] = [];
    const dataTransfer = new DataTransfer(); // to update the file selector
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      if (file.size < 10 * 1024 * 1024) {
        newFiles.push(file);
        dataTransfer.items.add(file);
      } else {
        this.utilityService.presentCustomErrorToast(
          `File "${file.name}" exceeds the size limit.`
        );
      }
    }
    this.documentsForm.patchValue({ files: newFiles });
    this.inputFile.nativeElement.files = dataTransfer.files;
  }

  deleteFile(_, i: number) {
    const newFiles = [...this.uploadFileList];
    const dataTransfer = new DataTransfer(); // to update the file selector
    newFiles.splice(i, 1);
    for (const newFile of newFiles) {
      dataTransfer.items.add(newFile);
    }
    this.documentsForm.patchValue({ files: newFiles });
    this.inputFile.nativeElement.files = dataTransfer.files;
  }

  onSubmit() {
    forkJoin(
      of(...this.uploadFileList).pipe(
        concatMap((file) => {
          const payload = {
            key: this.externalPatientChartId,
            label: this.documentsForm.get("name").value,
            serviceName: "PatientChart",
            fileExtension: file.name.slice(file.name.lastIndexOf(".")),
            description: this.documentsForm.get("description").value,
            category: this.documentsForm.get("category").value,
            patientID: this.externalPatientChartId,
          };
          const body = new FormData();
          body.append("file", file);
          body.append("tags", JSON.stringify(payload));

          return this.documentService.uploadDocument(body);
        })
      )
    )
      .finally(() => {
        this.decreaseSpinnerCount();
        this.documentsSelected = [];
        this.refreshDocumentList.emit(true);
        this.onCancel();
      })
      .subscribe(
        () => {
          this.utilityService.presentSuccessToast(
            `Uploaded ${this.uploadFileList.length} document(s).`
          );
        },
        (error) => {
          this.utilityService.presentCustomErrorToast(
            `Error occured while uploading document(s): ${error.message}.`
          );
        }
      );
    this.increaseSpinnerCount("Uploading documents...");
  }

  onCancel() {
    this.documentsForm.reset();
    this.inputFile.nativeElement.files = new DataTransfer().files;
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Loading...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  documentSelection(documentListSelected: number) {
    if (this.documentsSelected.includes(documentListSelected)) {
      this.documentsSelected = this.documentsSelected.filter(
        (d) => d !== documentListSelected
      );
    } else {
      this.documentsSelected.push(documentListSelected);
    }
  }

  generateSummary() {
    let popover = this.modalCtrl.create(
      GenerateAISummary,
      {
        documentsIds: this.documentsSelected,
        patientId: this.patientChartDetailsId,
      },
      { cssClass: "patient-summary-modal" }
    );
    popover.present({});
  }
}



// WEBPACK FOOTER //
// ./src/components/intake-queue/patient-documents/patient-documents.ts